<template>
	<article class="d-knowledge-container">
		<header
			v-if="title || name || teaser"
			class="text-white w-full px-layout-margin pt-[35px] mb-[50px]"
		>
			<h1
				v-if="title || name"
				class="text-45 font-light mb-[10px]"
				v-html="title || name"
			></h1>

			<p
				v-if="teaser"
				class="whitespace-pre-line text-22"
				v-html="teaser"
			></p>
		</header>

		<LimboSearch
			v-if="searchForm"
			v-slot="limboSearch"
			role="search"
			:search-filters="searchForm"
			:config="{
				allowSearchFiltersMutation: true,
				clearRouterHashOnSearch: true,
				updateVueRouteOnSearch: true,
			}"
			class=">=568:px-layout-margin"
			@update="updateSearchId"
		>
			<!-- We want the form there semantically, but not functionally -->
			<!-- Ie.: The form itself doesn't actually do anything! -->
			<form
				:action="action"
				:method="searchForm.method"
				class="d-knowledge-container__search-form text-white w-full p-box-padding grid grid-cols-1 gap-[17px]"
				@submit.prevent
			>
				<FilterText
					v-if="searchForm.fieldByName.text"
					v-model="tempTextValue"
					type="search"
					:name="searchForm.fieldByName.text.name"
					:placeholder="searchForm.fieldByName.text.placeholder"
					class="w-full"
					@submit="
						($event) => {
							searchForm.fieldByName.text.value = tempTextValue;
							limboSearch.action.submit($event);
						}
					"
				/>

				<FilterRadioList
					v-if="searchForm.fieldByName.category"
					v-model="searchForm.fieldByName.category.value"
					class="w-full <568:flex-col"
					label="Vis"
					:name="searchForm.fieldByName.category.name"
					:options="searchForm.fieldByName.category.items"
					:info-lookup-table="infoLookupTable"
					@change="limboSearch.action.submit"
				/>
			</form>

			<section
				aria-live="polite"
				:aria-busy="
					limboSearch.state?.isLoading ||
					!limboSearch.state?.isInitiated
						? 'true'
						: 'false'
				"
				class="relative"
			>
				<!-- Search head -->
				<SearchSpinner class="absolute left-[0px] top-[0px] w-full" />
				<header
					class="w-full px-layout-margin >=568:px-[0px] py-[25px] flex items-center text-white"
				>
					<!-- The search status -->
					<span
						v-if="
							limboSearch.state?.isLoading ||
							!limboSearch.state?.isInitiated
						"
						role="”status”"
						class=""
					>
						Søger...
					</span>
					<span v-else role="”status”" class="">
						Der blev fundet
						<strong
							v-text="limboSearch.pagination?.total ?? 0"
						></strong>
						{{
							limboSearch.pagination?.total === 1
								? 'dokument'
								: 'dokumenter'
						}}
					</span>

					<!-- The filter -->
					<span
						class="ml-auto hidden >=760:inline-flex items-center gap-[10px]"
					>
						<button
							class="w-[20px] h-[20px] aria-pressed:text-orange"
							:aria-pressed="layout === 'grid' ? 'true' : 'false'"
							aria-label="Vis som gitter"
							@click="() => setLayout('grid')"
						>
							<SvgIconLayoutGrid class="w-full h-full" />
						</button>
						<button
							class="w-[20px] h-[20px] aria-pressed:text-orange"
							:aria-pressed="layout === 'list' ? 'true' : 'false'"
							aria-label="Vis som liste"
							@click="() => setLayout('list')"
						>
							<SvgIconLayoutList class="w-full h-full" />
						</button>
					</span>
				</header>

				<!-- Results -->
				<div
					class="grid grid-cols-1 gap-layout-gutter w-full"
					:class="{
						'>=760:grid-cols-2 >=760:auto-rows-fr >=990:grid-cols-3 gap-layout-gutter':
							layout === 'grid',
					}"
				>
					<article
						v-for="(item, itemIndex) in limboSearch.data"
						:key="`${item.id}`"
						class="d-knowledge-container__item w-full flex flex-col p-box-padding bg-white"
						:class="{
							'd-knowledge-container__item--initial-load':
								!limboSearch.state.isAppend,
						}"
						:style="{
							'--delay': `${Math.min(itemIndex * 25, 1500)}ms`,
						}"
					>
						<!-- Heading -->
						<NuxtLink
							v-if="item.url && item.name"
							:to="item.url"
							class="inline-block underline text-text hover:text-opacity-80 transition-colors duration-300"
						>
							<h2
								class="text-16 font-bold"
								v-html="item.name"
							></h2>
						</NuxtLink>
						<h2
							v-else-if="item.name"
							class="text-16 font-bold"
							v-html="item.name"
						></h2>

						<!-- Text -->
						<div
							v-if="item.teaser"
							class="s-rich-text mt-paragraph"
							:class="{
								'>=760:hidden': layout === 'list',
							}"
							v-html="item.teaser"
						></div>

						<!-- Details -->
						<div class="flex items-center mt-[5px]">
							<!-- Tags -->
							<ul
								v-if="item.tags?.length"
								class="d-knowledge-container__tags text-12 text-blue"
							>
								<li
									v-for="(tag, tagIndex) in item.tags"
									:key="tagIndex"
									class="inline"
									v-text="tag"
								></li>
							</ul>

							<!-- Filetype -->
							<span
								v-if="item.type"
								class="ml-auto inline-flex w-fit px-[9px] py-[4px] flex-shrink-0 font-bold text-12 text-orange uppercase border border-current rounded-[3px]"
							>
								{{ item.type }}
							</span>
						</div>
					</article>
				</div>

				<!-- Load more -->
				<footer
					v-if="limboSearch.state?.hasMoreItems"
					class="w-full flex justify-center items-center py-[20px]"
				>
					<BaseButton
						:aria-disabled="
							limboSearch.state?.isLoading ||
							!limboSearch.state?.isInitiated
								? 'true'
								: 'false'
						"
						@click="() => limboSearch.action.fetchMore()"
						v-text="'Vis flere'"
					/>
				</footer>
			</section>
		</LimboSearch>
	</article>
</template>

<script>
export default {
	inheritAttrs: false,
};
</script>

<script setup>
import { useLimboForm } from '~/composables/useLimboForm';
import SvgIconLayoutGrid from '~/assets/svgs/icon-layout-grid.svg';
import SvgIconLayoutList from '~/assets/svgs/icon-layout-list.svg';

const props = defineProps({
	title: String,
	name: String,
	teaser: String,
	searchForm: Object,
	infoLookupTable: Object,
});

const searchForm = ref(
	useLimboForm(props.searchForm, { populateFromQuery: true })
);
const tempTextValue = ref(searchForm.value.fieldByName.text?.value ?? '');
const action = ref(null);
onMounted(() => {
	action.value = searchForm.value.endpointUrl;
});

const layout = ref('grid');
const setLayout = (newLayout) => {
	layout.value = newLayout;
};
const searchId = ref(0);
const updateSearchId = (searchInfo) => {
	if (!searchInfo?.state?.isLoading) {
		return ++searchId.value;
	}
};
</script>

<style lang="postcss">
.d-knowledge-container {
	@apply w-full max-w-layout-max pb-[80px] mx-auto;
}

.d-knowledge-container__search-form {
	@apply bg-cyan;
	background-image: linear-gradient(#046c72, #05868e);
}

.d-knowledge-container__tags {
	&:before {
		content: 'Emne: ';
	}
	li:not(:last-child):after {
		content: ', ';
	}
}

.d-knowledge-container__item--initial-load {
	opacity: 0;
	animation: d-knowledge-container__item 300ms var(--delay, 0ms) ease-out forwards;
}
@keyframes d-knowledge-container__item {
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
}
</style>
